import React, { useState, useEffect, useRef } from "react";
import { useFinance } from "../context/FinanceContext";
import "./StatCards.css";

function fmt(n) {
  return "₹" + Number(n).toLocaleString("en-IN");
}

const COMPANIES = [
  { key: "Melvine Solution", short: "MS", color: "#8567df" },
  { key: "Amara Infinites",  short: "AM", color: "#fbbf24" },
  { key: "Ragnaor",          short: "RA", color: "#34d399" },
  { key: "Personal",         short: "PE", color: "rgb(247, 46, 187)" },
];

// ── Responsive pie size hook ──────────────────────────────────────────────
function usePieSize() {
  const [size, setSize] = useState(100);
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w <= 380)       setSize(72);
      else if (w <= 560)  setSize(88);
      else if (w <= 768)  setSize(96);
      else                setSize(100);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return size;
}

// ── Pie Chart ─────────────────────────────────────────────────────────────
function PieChart({ pieData = [], size = 100 }) {
  const canvasRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const slicesRef = useRef([]);

  const totals = pieData.map((c) => ({ ...c, value: c.amount || 0 }));
  const total = totals.reduce((s, c) => s + c.value, 0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const sz = canvas.width;
    const cx = sz / 2, cy = sz / 2, r = sz / 2 - 2;

    ctx.clearRect(0, 0, sz, sz);
    slicesRef.current = [];

    if (!total) {
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255,255,255,0.20)";
      ctx.fill();
      return;
    }

    let startAngle = -Math.PI / 2;
    totals.forEach((c) => {
      if (!c.value) return;
      const sweep = (c.value / total) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r, startAngle, startAngle + sweep);
      ctx.closePath();
      ctx.fillStyle = c.color;
      ctx.fill();
      slicesRef.current.push({ ...c, startAngle, endAngle: startAngle + sweep });
      startAngle += sweep;
    });
  }, [total, pieData, size]);

  const handleMouseMove = (e) => {
    const canvas = canvasRef.current;
    if (!canvas || !total) return;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const sz = canvas.width;
    const cx = sz / 2, cy = sz / 2;
    const dx = mx - cx, dy = my - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > sz / 2 - 2) { setTooltip(null); return; }
    let angle = Math.atan2(dy, dx);
    if (angle < -Math.PI / 2) angle += Math.PI * 2;
    const hit = slicesRef.current.find((s) => angle >= s.startAngle && angle < s.endAngle);
    if (hit) {
      setTooltip({ x: mx, y: my, name: hit.key, amount: hit.value, color: hit.color });
    } else {
      setTooltip(null);
    }
  };

  // Touch support for pie tooltip
  const handleTouchStart = (e) => {
    const canvas = canvasRef.current;
    if (!canvas || !total) return;
    const touch = e.touches[0];
    const rect = canvas.getBoundingClientRect();
    const mx = touch.clientX - rect.left;
    const my = touch.clientY - rect.top;
    const sz = canvas.width;
    const cx = sz / 2, cy = sz / 2;
    const dx = mx - cx, dy = my - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > sz / 2 - 2) { setTooltip(null); return; }
    let angle = Math.atan2(dy, dx);
    if (angle < -Math.PI / 2) angle += Math.PI * 2;
    const hit = slicesRef.current.find((s) => angle >= s.startAngle && angle < s.endAngle);
    if (hit) {
      setTooltip({ x: mx, y: my, name: hit.key, amount: hit.value, color: hit.color });
      setTimeout(() => setTooltip(null), 2000);
    }
  };

  return (
    <div className="sc-pie-wrapper">
      <canvas
        ref={canvasRef}
        width={size}
        height={size}
        className="sc-pie-canvas"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
        onTouchStart={handleTouchStart}
      />
      {tooltip && (
        <div className="sc-pie-tooltip" style={{ left: tooltip.x + 8, top: tooltip.y - 10 }}>
          <span className="sc-pie-tooltip-dot" style={{ background: tooltip.color }} />
          <span className="sc-pie-tooltip-name">{tooltip.name}</span>
          <span className="sc-pie-tooltip-amt">{fmt(tooltip.amount)}</span>
        </div>
      )}
    </div>
  );
}

// ── Add / Edit Expense Modal ───────────────────────────────────────────────
function AddExpenseModal({ categories, existing, onClose, onSave }) {
  const [form, setForm] = useState({
    label:    existing?.label    || "",
    amount:   existing?.amount   || "",
    category: existing?.category || categories[0]?.name || "",
    dueDate:  existing?.dueDate  || "",
  });
  const [error, setError] = useState("");
  const isEdit = !!existing;

  // Close on Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleSave = () => {
    if (!form.label.trim()) return setError("Please enter an expense label.");
    if (!form.amount || parseFloat(form.amount) <= 0)
      return setError("Please enter a valid amount.");
    setError("");
    onSave({
      ...(isEdit ? { id: existing.id } : {}),
      purpose: form.label.trim(), label: form.label.trim(),
      amount: parseFloat(form.amount), category: form.category,
      date: form.dueDate || new Date().toISOString().split("T")[0],
      dueDate: form.dueDate || new Date().toISOString().split("T")[0],
    });
  };

  return (
    <div className="modal-overlay modal-overlay--top" onClick={onClose}>
      <div className="add-expense-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="add-expense-modal-header">
          <div className="add-expense-modal-title">
            <i className={`fa-solid ${isEdit ? "fa-pen" : "fa-circle-minus"}`}></i>
            {isEdit ? "Edit Expense" : "Add Expense"}
          </div>
          <button className="aem-close-btn" onClick={onClose}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div className="add-expense-modal-body">
          {error && (
            <div className="add-expense-error">
              <i className="fa-solid fa-triangle-exclamation"></i> {error}
            </div>
          )}
          <div className="aem-form-group">
            <label><i className="fa-solid fa-tag"></i> Expense Label</label>
            <input type="text" placeholder="e.g. Monthly Rent" value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })} autoFocus />
          </div>
          <div className="aem-form-row">
            <div className="aem-form-group">
              <label><i className="fa-solid fa-indian-rupee-sign"></i> Amount</label>
              <input type="number" inputMode="decimal" placeholder="0" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </div>
            <div className="aem-form-group">
              <label><i className="fa-solid fa-building"></i> Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div className="aem-form-group">
            <label>
              <i className="fa-solid fa-calendar"></i> Due Date
              <span className="aem-optional"> (optional)</span>
            </label>
            <input type="date" value={form.dueDate}
              onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
          </div>
          {form.label && form.amount && (
            <div className="aem-preview">
              <i className="fa-solid fa-receipt"></i>
              <span className="aem-preview-label">{form.label}</span>
              <span className="aem-preview-amt">-{fmt(parseFloat(form.amount) || 0)}</span>
            </div>
          )}
        </div>
        <div className="add-expense-modal-footer">
          <button className="aem-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="aem-btn-save" onClick={handleSave}>
            <i className={`fa-solid ${isEdit ? "fa-floppy-disk" : "fa-plus"}`}></i>
            {isEdit ? "Save Changes" : "Add Expense"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteConfirmModal({ expense, onCancel, onConfirm }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel]);

  return (
    <div className="modal-overlay modal-overlay--top" onClick={onCancel}>
      <div className="delete-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="delete-modal-icon"><i className="fa-solid fa-trash-can"></i></div>
        <h3 className="delete-modal-title">Delete Expense?</h3>
        <p className="delete-modal-desc">
          Are you sure you want to delete <strong>{expense.label}</strong>?<br />
          <span className="delete-modal-amt">{fmt(expense.amount)}</span>
        </p>
        <div className="delete-modal-actions">
          <button className="delete-cancel-btn" onClick={onCancel}>Cancel</button>
          <button className="delete-confirm-btn" onClick={onConfirm}>
            <i className="fa-solid fa-trash-can"></i> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Full Awaiting Modal ───────────────────────────────────────────────────
function AwaitingModal({ expenses, categories, dispatch, onClose, onAdd }) {
  const [editTarget, setEditTarget]     = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const total = expenses.reduce((s, e) => s + e.amount, 0);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleSaveEdit = (payload) => {
    dispatch({
      type: "EDIT_EXPENSE",
      payload: {
        id: payload.id, label: payload.label,
        amount: payload.amount, category: payload.category, dueDate: payload.dueDate,
      },
    });
    setEditTarget(null);
  };

  const handleConfirmDelete = () => {
    dispatch({ type: "DELETE_EXPENSE", payload: deleteTarget.id });
    setDeleteTarget(null);
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="awaiting-modal-box" onClick={(e) => e.stopPropagation()}>
          <div className="awaiting-modal-header">
            <div className="awaiting-modal-title">
              <i className="fa-solid fa-clock-rotate-left"></i>
              Awaiting Expenses
              <span className="awaiting-modal-count">{expenses.length}</span>
            </div>
            <div className="awaiting-modal-header-right">
              <button className="awaiting-modal-add-btn" onClick={onAdd}>
                <i className="fa-solid fa-plus"></i> Add Expense
              </button>
              <button className="awaiting-modal-close" onClick={onClose}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
          </div>
          <div className="awaiting-modal-summary">
            <div className="summary-stat">
              <span className="summary-stat__label">Total Pending</span>
              <span className="summary-stat__value red">{fmt(total)}</span>
            </div>
            <div className="summary-stat">
              <span className="summary-stat__label">Items</span>
              <span className="summary-stat__value">{expenses.length}</span>
            </div>
          </div>
          <div className="awaiting-modal-list">
            {expenses.length === 0 ? (
              <div className="awaiting-empty">
                <div className="awaiting-empty-icon">
                  <i className="fa-solid fa-circle-check"></i>
                </div>
                <p>All caught up! No pending expenses.</p>
              </div>
            ) : (
              expenses.map((e) => (
                <AwaitingRowFull
                  key={e.id} e={e} dispatch={dispatch}
                  onEdit={setEditTarget} onDelete={setDeleteTarget}
                />
              ))
            )}
          </div>
        </div>
      </div>
      {editTarget && (
        <AddExpenseModal categories={categories} existing={editTarget}
          onClose={() => setEditTarget(null)} onSave={handleSaveEdit} />
      )}
      {deleteTarget && (
        <DeleteConfirmModal expense={deleteTarget}
          onCancel={() => setDeleteTarget(null)} onConfirm={handleConfirmDelete} />
      )}
    </>
  );
}

// Row with full controls (used inside full modal)
function AwaitingRowFull({ e, dispatch, onEdit, onDelete }) {
  return (
    <div className="awaiting-row">
      <div className="awaiting-icon"><i className="fa-solid fa-receipt"></i></div>
      <div className="awaiting-info">
        <span className="awaiting-label">{e.label}</span>
        <span className="awaiting-meta">{e.category}{e.dueDate ? ` · Due ${e.dueDate}` : ""}</span>
      </div>
      <span className="awaiting-amt">{fmt(e.amount)}</span>
      <button className="row-edit-btn" title="Edit" onClick={() => onEdit(e)}>
        <i className="fa-solid fa-pen"></i>
      </button>
      <button className="row-delete-btn" title="Delete" onClick={() => onDelete(e)}>
        <i className="fa-solid fa-trash-can"></i>
      </button>
      <button className="mark-done-btn" title="Mark as done"
        onClick={() => dispatch({ type: "COMPLETE_EXPENSE", payload: e.id })}>
        <i className="fa-solid fa-check"></i>
      </button>
    </div>
  );
}

// ── Awaiting card body ────────────────────────────────────────────────────
function AwaitingCardBody({ pieData, pieSize }) {
  return (
    <>
      <div className="stat-card__divider" />
      <div className="awaiting-pie-row">
        <div className="awaiting-pie-left">
          <PieChart pieData={pieData} size={pieSize} />
        </div>
        <div className="awaiting-pie-right">
          <div className="card-indicators">
            {COMPANIES.map((c) => (
              <div className="card-indicator" key={c.key}>
                <span className="card-indicator-dot" style={{ background: c.color }} />
                <span className="card-indicator-name">{c.short}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────
export default function StatCards() {
  const { state, dispatch, derived } = useFinance();
  const [showAwaitingModal, setShowAwaitingModal] = useState(false);
  const [showAddExpense, setShowAddExpense]       = useState(false);
  const pieSize = usePieSize();

  const handleSaveNewExpense = (payload) => {
    dispatch({ type: "ADD_SPEND", payload });
    setShowAddExpense(false);
  };

  // Build per-company pie data for each card
  const reserveData = COMPANIES.map((c) => ({
    ...c,
    amount: state.reserveCategories.filter((r) => r.office === c.key).reduce((s, r) => s + r.amount, 0),
  }));

  const incomeData = COMPANIES.map((c) => ({
    ...c,
    amount: state.incomes.filter((i) => (i.office || i.company) === c.key).reduce((s, i) => s + i.amount, 0),
  }));

  const generatingData = COMPANIES.map((c) => ({
    ...c,
    amount: state.incomes
      .filter((i) => (i.office || i.company) === c.key)
      .reduce((s, i) => {
        const allocated = (i.allocated || []).reduce((a, al) => a + al.amount, 0);
        return s + Math.max(0, i.amount - allocated);
      }, 0),
  }));

  const completedData = COMPANIES.map((c) => ({
    ...c,
    amount: state.completedExpenses.filter((e) => e.category === c.key).reduce((s, e) => s + e.amount, 0),
  }));

  const awaitingData = COMPANIES.map((c) => ({
    ...c,
    amount: state.awaitingExpenses.filter((e) => e.category === c.key).reduce((s, e) => s + e.amount, 0),
  }));

  const cards = [
    { id: "reserve",    label: "Reserve",           value: fmt(derived.totalReserve),     color: "dark-green",  pieData: reserveData    },
    { id: "generating", label: "Generating",         value: fmt(derived.generatingIncome), color: "light-green", pieData: generatingData },
    { id: "income",     label: "Income",             value: fmt(derived.totalIncome),      color: "blue",        pieData: incomeData     },
    { id: "completed",  label: "Completed Expenses", value: fmt(derived.totalCompleted),   color: "light-red",   pieData: completedData  },
  ];

  return (
    <>
      <div className="stat-cards-grid">
        {/* 4 regular cards */}
        {cards.map((card) => (
          <div className={`stat-card stat-card--${card.color}`} key={card.id}>
            <div className="stat-card__title">{card.label}</div>
            <div className="stat-card__value">{card.value}</div>
            <div className="stat-card__divider" />
            <div className="pie-row">
              <div className="pie-left">
                <PieChart pieData={card.pieData} size={pieSize} />
              </div>
              <div className="pie-right">
                <div className="card-indicators">
                  {COMPANIES.map((c) => (
                    <div className="card-indicator" key={c.key}>
                      <span className="card-indicator-dot" style={{ background: c.color }} />
                      <span className="card-indicator-name">{c.short}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Awaiting card */}
        <div className="stat-card stat-card--hard-red">
          <div className="stat-card__title">Awaiting Expenses</div>
          <div className="awaiting-value-row">
            <span className="stat-card__value">{fmt(derived.totalAwaiting)}</span>
            <button
              className="three-dot-btn-h"
              title="View all expenses"
              onClick={() => setShowAwaitingModal(true)}
            >
              <span /><span /><span />
            </button>
          </div>
          <AwaitingCardBody
            pieData={awaitingData}
            pieSize={pieSize}
            dispatch={dispatch}
            onViewAll={() => setShowAwaitingModal(true)}
            onAdd={() => setShowAddExpense(true)}
          />
        </div>
      </div>

      {showAwaitingModal && (
        <AwaitingModal
          expenses={state.awaitingExpenses}
          categories={COMPANIES.map((c, i) => ({ id: i, name: c.key }))}
          dispatch={dispatch}
          onClose={() => setShowAwaitingModal(false)}
          onAdd={() => setShowAddExpense(true)}
        />
      )}
      {showAddExpense && (
        <AddExpenseModal
          categories={COMPANIES.map((c, i) => ({ id: i, name: c.key }))}
          onClose={() => setShowAddExpense(false)}
          onSave={handleSaveNewExpense}
        />
      )}
    </>
  );
}